import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { usePage } from "@inertiajs/react"
import { useEffect, useState } from "react"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import type { Appearance } from "@/hooks/use-appearance"

function isDark() {
  return typeof document !== "undefined" && document.documentElement.classList.contains("dark")
}

// shadcn generates this against next-themes, which this app doesn't use —
// going through it without its provider would leave toasts following the OS
// rather than the in-app toggle. useAppearance isn't the answer either: it
// holds component-local state, so this instance would freeze at mount and miss
// a toggle made on the Account page. Watch the class the toggle actually sets.
//
// Starts from the same `appearance` prop use-appearance.ts reads, not
// isDark() — the server can't check the DOM, so starting from the DOM would
// make the server and browser disagree on first render. The effect below
// then syncs to the real DOM once mounted, to catch a first-time visitor
// whose OS prefers dark but has no saved preference yet.
function useDarkClass() {
  const shared = usePage<{ appearance?: Appearance }>().props.appearance
  const [dark, setDark] = useState(shared === "dark")

  useEffect(() => {
    setDark(isDark())
    const root = document.documentElement
    const observer = new MutationObserver(() => setDark(isDark()))
    observer.observe(root, { attributes: true, attributeFilter: ["class"] })
    return () => observer.disconnect()
  }, [])

  return dark
}

const Toaster = ({ ...props }: ToasterProps) => {
  const appearance = useDarkClass() ? "dark" : "light"

  return (
    <Sonner
      theme={appearance}
      className="toaster group"
      // Semantic colour rides on the icon, not the surface. Confirmations
      // ("Draft saved.") fire on nearly every write, and a filled toast each
      // time drowns out the errors that actually need attention. Shape carries
      // the meaning too — circle-check vs octagon-x — so this doesn't depend
      // on colour alone.
      icons={{
        success: <CircleCheckIcon className="size-4 text-success" />,
        info: <InfoIcon className="size-4 text-primary" />,
        warning: <TriangleAlertIcon className="size-4 text-accent" />,
        error: <OctagonXIcon className="size-4 text-destructive" />,
        loading: <Loader2Icon className="size-4 animate-spin text-muted-foreground" />,
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
